/**
 * Browser client for the SFRA Insiders claim endpoints (COM-506).
 *
 * Both calls run in the BROWSER, same-origin, through the Worker's
 * `/on/demandware.store` proxy — never from a loader/action. The CSRF token
 * `Loyalty-FormData` mints is bound to the browser's own `dwsid`; a token
 * fetched by the Worker belongs to a different session and fails
 * `validateAjaxRequest` (same reason as `use-sfra-save-payment.ts`).
 *
 * `X-Requested-With: XMLHttpRequest` is required: without it SFRA answers a
 * server error as an HTML page instead of `Error-Start` JSON. `cache:
 * "no-store"` guards the per-user, token-bearing GET against any intermediate
 * cache. Every call carries an `upstreamSignal` (critical rule 8).
 */
import { buildSfraControllerPath } from "~/hec/utils/sfra-url";
import { upstreamSignal } from "~/utils/upstream-timeouts";

import {
  FORM_DATA_ACTION,
  classifyClaimResponse,
} from "./insiders-claim.logic";
import { formDataResponseSchema } from "./insiders-claim.schemas";
import type { ClaimOutcome, FormDataResult } from "./types";

const SFRA_AJAX_HEADER = { "X-Requested-With": "XMLHttpRequest" } as const;

/**
 * One `Loyalty-FormData` request per site per page, shared by every claim card
 * mounted on it (the Insiders page mounts three). The first proxied GET after a
 * Workers login is also the request that rebuilds SFRA's registered session,
 * so letting three cards race it is worse than making them wait on one.
 * A failed request is evicted so the next caller retries.
 */
const inflight = new Map<string, Promise<FormDataResult>>();

/** Drop the shared `Loyalty-FormData` result — after a login flip or a CSRF failure. */
export function invalidateInsidersFormData(siteId?: string): void {
  if (siteId === undefined) inflight.clear();
  else inflight.delete(siteId);
}

export function fetchInsidersFormData(siteId: string): Promise<FormDataResult> {
  const cached = inflight.get(siteId);
  if (cached) return cached;
  const request = requestFormData(siteId).then(result => {
    if (result.kind !== "ok") inflight.delete(siteId);
    return result;
  });
  inflight.set(siteId, request);
  return request;
}

async function requestFormData(siteId: string): Promise<FormDataResult> {
  try {
    const response = await fetch(
      buildSfraControllerPath(siteId, FORM_DATA_ACTION),
      {
        credentials: "same-origin",
        cache: "no-store",
        headers: SFRA_AJAX_HEADER,
        signal: upstreamSignal("sfra-proxy"),
      },
    );
    if (!response.ok) return { kind: "http", status: response.status };
    const json: unknown = await response.json().catch(() => undefined);
    const parsed = formDataResponseSchema.safeParse(json);
    return parsed.success
      ? { kind: "ok", data: parsed.data }
      : { kind: "malformed" };
  } catch {
    return { kind: "network" };
  }
}

/**
 * `POST Loyalty-RedeemPoints`. Never throws — every failure is an outcome the
 * UI can render. The write budget applies: aborting mid-request could leave
 * points credited at Annex while the shopper is told it failed.
 */
export async function submitInsidersClaim(
  actionUrl: string,
  body: URLSearchParams,
): Promise<ClaimOutcome> {
  let response: Response;
  try {
    response = await fetch(actionUrl, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...SFRA_AJAX_HEADER,
      },
      body: body.toString(),
      signal: upstreamSignal("sfra-proxy-write"),
    });
  } catch {
    return { kind: "network" };
  }
  const json: unknown = await response.json().catch(() => null);
  return classifyClaimResponse(response.status, json);
}
